import React, { useEffect, useState } from "react";
import { Box, Typography, Switch, CircularProgress, Button, Divider } from "@mui/material";
import { Add } from "@mui/icons-material";
import { toast } from "react-toastify";
import FormDrawer from "components/Shared/FormDrawer";
import ShipmentTransitBranchForm from "./ShipmentTransitBranchForm";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import { useAuth } from "hooks";
import {
  useAddShipmentTransitBranchMutation,
  useLazyListShipmentTransitBranchesQuery,
  useUpdateShipmentTransitBranchMutation,
} from "store/services/shipmentApi";
import RecordCard from "./RecordCard";

const FieldRow = ({ label, children, value }) => (
  <Box sx={{ display: "flex", py: 0.25, alignItems: "center" }}>
    <Box sx={{ width: "40%", color: "text.primary", fontSize: "0.7rem", fontWeight: 600 }}>{label}</Box>
    <Box sx={{ width: "60%", fontSize: "0.74rem", fontWeight: 500 }}>
      {children ? children : value ?? "N/A"}
    </Box>
  </Box>
);

const TransitBranchRecordCard = ({ row, onToggleStatus, disabled, updating }) => (
  <RecordCard>
    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
      {row?.transitBranch?.name || "N/A"}
    </Typography>
    <Divider sx={{ my: 0.5 }} />
    <FieldRow label="Branch Code" value={row?.transitBranch?.code} />
    <FieldRow label="Status">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 600 }} color={row.isActive ? "success.main" : "error.main"}>
          {row.isActive ? "Active" : "Inactive"}
        </Typography>
        <Switch
          checked={row.isActive === true}
          onChange={() => onToggleStatus(row)}
          size="small"
          disabled={disabled || updating}
        />
      </Box>
    </FieldRow>
  </RecordCard>
);

const ShipmentTransitBranchCompact = () => {
  const { isCustomer, isVendor, isContractor, isDriver, isVendorStaff } = useAuth();
  const canEdit = isCustomer || isVendor || isContractor || isDriver || isVendorStaff;
  const { ShipmentDetail } = useShipmentDetailContext();
  const shipmentId = ShipmentDetail?._id;

  const [showTransitForm, setShowTransitForm] = useState(false);
  const [fetchTransitBranches, { data: transitBranchesRes, isLoading, isFetching }] =
    useLazyListShipmentTransitBranchesQuery();
  const [addTransitBranch, { isLoading: addingTransitBranch }] =
    useAddShipmentTransitBranchMutation();
  const [updateTransitBranch, { isLoading: updatingTransitBranch }] =
    useUpdateShipmentTransitBranchMutation();

  useEffect(() => {
    if (shipmentId) fetchTransitBranches({ shipmentId });
  }, [shipmentId, fetchTransitBranches]);

  const handleCancelTransit = () => setShowTransitForm(false);

  const handleAddTransitBranch = async (data) => {
    try {
      if (!data.transitBranch) {
        toast.error("Transit branch is required");
        return;
      }
      await addTransitBranch({
        shipmentId: data.shipmentId,
        transitBranch: data.transitBranch,
      }).unwrap();
      toast.success("Transit branch added successfully!");
      handleCancelTransit();
      fetchTransitBranches({ shipmentId });
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't add transit branch!");
    }
  };

  const handleStatusChange = async (row) => {
    try {
      if (!shipmentId || !row?._id) {
        toast.error("Shipment details or transit branch ID not available");
        return;
      }
      const newStatus = !row.isActive;
      await updateTransitBranch({
        transitBranchId: row._id,
        shipmentId,
        isActive: newStatus,
      }).unwrap();
      toast.success(`Transit branch ${newStatus ? "activated" : "deactivated"} successfully!`);
      fetchTransitBranches({ shipmentId });
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update transit branch status!");
    }
  };

  const records = Array.isArray(transitBranchesRes?.data) ? transitBranchesRes.data : [];

  return (
    <Box sx={{ p: 1 }}>
      {isLoading || isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={18} />
        </Box>
      ) : records.length === 0 ? (
        <Typography sx={{ fontSize: "0.74rem", color: "text.secondary", textAlign: "center", py: 2 }}>
          No transit branches found.
        </Typography>
      ) : (
        records.map((row) => (
          <TransitBranchRecordCard
            key={row._id}
            row={row}
            onToggleStatus={handleStatusChange}
            disabled={ShipmentDetail?.isActive === false}
            updating={updatingTransitBranch}
          />
        ))
      )}
      <FormDrawer title="Add Transit Branch" open={showTransitForm} onClose={handleCancelTransit}>
        <ShipmentTransitBranchForm
          isBusy={addingTransitBranch}
          shipmentId={shipmentId}
          onCancel={handleCancelTransit}
          onSubmit={handleAddTransitBranch}
        />
      </FormDrawer>
    </Box>
  );
};

export default ShipmentTransitBranchCompact;